import {CommonModule} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Component, Inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {BACKEND_URL} from '@aztrix/environment';
import {hasVerification, HelperPipesModule} from '@aztrix/helpers';
import {
  AgreementDataRepresentation,
  AgreementRepresentation,
  ProfileRepresentation,
  ProposalRepresentation,
} from '@aztrix/sdk';
import {TranslatePipe} from '@aztrix/translate';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
} from 'rxjs';

import {AgreementVerificationStatusComponent} from '../agreement-verification-status/agreement-verification-status.component';

@Component({
  selector: 'ax-agreement-dates',
  standalone: true,
  imports: [CommonModule, HelperPipesModule, TranslatePipe, AgreementVerificationStatusComponent],
  templateUrl: './agreement-dates.component.html',
  styleUrls: ['../property.scss', './agreement-dates.component.scss'],
})
export class AgreementDatesComponent implements OnChanges {
  @Input() agreement?: AgreementRepresentation;
  @Input() agreementData?: AgreementDataRepresentation;
  @Input() proposal?: ProposalRepresentation;
  @Input() myProfile?: ProfileRepresentation;

  private _agreement$ = new ReplaySubject<AgreementRepresentation | undefined>(undefined);
  private _agreementData$ = new ReplaySubject<AgreementDataRepresentation | undefined>(undefined);
  private _myProfile$ = new ReplaySubject<ProfileRepresentation | undefined>(undefined);

  hasAgreementDataToDisplay$ = this._agreementData$.pipe(
    map((d) => !!d && (d.agreementProperties?.length || 0) > 0 && d.confirmed !== undefined)
  );

  startTimestamp$ = this._agreementData$.pipe(
    filter((d) => !!d),
    map((d) => d?.startTimestamp)
  );

  isSubscriber$ = combineLatest([this._myProfile$, this._agreement$]).pipe(
    map(([myProfile, agreement]) => myProfile?.id === agreement?.subscriberId)
  );

  agreementCreator$ = this._agreementData$.pipe(
    switchMap((agreementData) => this._getProfile(agreementData?.creatorId)),
    shareReplay(1)
  );

  createdBySubject$ = this._agreementData$.pipe(
    map((agreementData) => {
      if (agreementData?.createdBy === 'OWNER') {
        return 'agreement.owner';
      } else if (agreementData?.createdBy === 'SUBSCRIBER') {
        return 'agreement.subscriber';
      } else {
        return '';
      }
    })
  );

  isHistoryAgreement$: Observable<boolean> = combineLatest([
    this._agreementData$.pipe(filter((d) => !!d)),
    this._agreement$,
  ]).pipe(
    map(
      ([agreementData, agreement]) =>
        agreementData?.startTimestamp !== agreement?.agreementData?.startTimestamp
    )
  );

  constructor(
    private _http: HttpClient,
    @Inject(BACKEND_URL) private _backendUrl$: BehaviorSubject<string>
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.agreement && changes.agreement) {
      this._agreement$.next(this.agreement);
    }

    if (this.agreementData && changes.agreementData) {
      this._agreementData$.next(this.agreementData);
    }

    if (this.myProfile && changes.myProfile) {
      this._myProfile$.next(this.myProfile);
    }
  }

  get proposalHasVerification(): boolean {
    return hasVerification(this.proposal);
  }

  private _getProfile(
    profileId: string | undefined
  ): Observable<ProfileRepresentation | undefined> {
    if (!profileId) {
      return of(undefined);
    }
    return this._backendUrl$.pipe(
      switchMap((backendUrl) =>
        this._http.get<ProfileRepresentation>(`${backendUrl || ''}/r/profiles/${profileId}`)
      )
    );
  }
}
